import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Dialog as DialogPrimitive } from 'radix-ui';

import { cn } from '@/shared/libs/utils/index';

const DialogRoot = DialogPrimitive.Root;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
));

DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ children, className, ...props }, ref) => (
  <DialogPrimitive.Content
    ref={ref}
    className={cn(
      'fixed left-[50%] top-[50%] z-50 flex w-[calc(100vw-1.5rem)] max-h-[90dvh] max-w-lg translate-x-[-50%] translate-y-[-50%] flex-col gap-4 overflow-hidden rounded-2xl border border-border/60 bg-card p-4 shadow-2xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:w-full sm:p-6',
      className,
    )}
    {...props}>
    {children}
  </DialogPrimitive.Content>
));

DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col space-y-1.5 text-center sm:text-left',
      className,
    )}
    {...props}
  />
);

DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
      className,
    )}
    {...props}
  />
);

DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'text-lg font-semibold leading-none tracking-tight',
      className,
    )}
    {...props}
  />
));

DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));

DialogDescription.displayName = DialogPrimitive.Description.displayName;

// Wrapper component matching ui-kit API
interface DialogWrapperProps extends React.PropsWithChildren {
  className?: string;
  closed?: boolean;
  footerSlot?: React.ReactNode;
  onClose?: () => void;
  progress?: number;
  title?: string;
  width?: number;
}

const DialogWrapper = React.forwardRef<HTMLDivElement, DialogWrapperProps>(
  (
    {
      children,
      className,
      closed: _closed,
      footerSlot,
      onClose,
      progress,
      title,
      width,
    },
    ref,
  ) => {
    const { t } = useTranslation();

    return (
      <DialogRoot
        defaultOpen
        open
        onOpenChange={(open) => {
          if (!open && onClose) {
            onClose();
          }
        }}>
        <DialogPortal>
          <DialogOverlay className="z-[9998]" />
          <DialogContent
            ref={ref}
            className={cn(
              'z-[9999]',
              typeof progress === 'number' && 'gap-0 p-0 sm:p-0',
              className,
            )}
            style={
              width
                ? { maxWidth: `min(${width}px, calc(100vw - 1.5rem))` }
                : undefined
            }
            onEscapeKeyDown={(e) => {
              e.preventDefault();
              onClose?.();
            }}
            onFocusOutside={(event) => {
              event.preventDefault();
            }}
            onPointerDownOutside={(e) => {
              e.preventDefault();
              onClose?.();
            }}>
            <DialogClose
              className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none z-10"
              onClick={onClose}>
              <X className="h-4 w-4" />
              <span className="sr-only">{t('buttons.close')}</span>
            </DialogClose>
            {title && typeof progress === 'number' && (
              <div className="shrink-0 border-b border-border/40">
                <DialogHeader className="px-4 py-3.5 sm:px-6">
                  <DialogTitle>{title}</DialogTitle>
                </DialogHeader>
                <div className="h-1.5 w-full bg-foreground/10">
                  <div
                    className="h-full bg-gradient-to-r from-indigo-500 to-violet-600 transition-all duration-300"
                    style={{
                      width: `${Math.min(100, Math.max(0, progress))}%`,
                    }}
                  />
                </div>
              </div>
            )}
            {title && typeof progress !== 'number' && (
              <DialogHeader>
                <DialogTitle>{title}</DialogTitle>
              </DialogHeader>
            )}
            {children}
            {footerSlot}
          </DialogContent>
        </DialogPortal>
      </DialogRoot>
    );
  },
);

DialogWrapper.displayName = 'DialogWrapper';

// DialogFooter wrapper matching ui-kit API
interface DialogFooterWrapperProps extends React.HTMLAttributes<HTMLDivElement> {
  endSlot?: React.ReactNode;
  fullWidth?: boolean;
  startSlot?: React.ReactNode;
}

const DialogFooterWrapper = ({
  children,
  className,
  endSlot,
  fullWidth,
  startSlot,
  ...props
}: Readonly<DialogFooterWrapperProps>) => (
  <DialogFooter
    className={cn(
      'flex flex-col-reverse gap-2 sm:flex-row sm:justify-between',
      fullWidth && 'w-full',
      className,
    )}
    {...props}>
    {startSlot}
    {endSlot ?? children}
  </DialogFooter>
);

DialogFooterWrapper.displayName = 'DialogFooterWrapper';

export {
  DialogWrapper as Dialog,
  DialogContent,
  DialogDescription,
  DialogFooterWrapper as DialogFooter,
  DialogHeader,
  DialogTitle,
};
